import { Component, computed, input } from '@angular/core';
import { BaseFormControl, BaseSignalInput } from '@vallift/ngl';
import {
  ChoiceCollectionData,
  ChoiceOption,
} from '../single-choice/single-choice';

export type MultiChoiceValue = string[] | ChoiceCollectionData[];

export function choiceOptionToAnswer(option: ChoiceOption): ChoiceCollectionData {
  if (option.data) {
    return {
      id: option.data.id,
      uniqueKey: option.data.uniqueKey,
      display: option.data.display,
      ...(option.data.secondaryDisplay != null
        ? { secondaryDisplay: option.data.secondaryDisplay }
        : {}),
    };
  }
  return {
    id: option.value,
    uniqueKey: option.value,
    display: option.label,
  };
}

export function isMultiChoiceAnswer(
  value: unknown,
): value is ChoiceCollectionData[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        item != null &&
        'id' in item &&
        typeof (item as ChoiceCollectionData).id === 'string',
    )
  );
}

export function isMultiChoiceStringAnswer(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}

/** Expand saved default keys into answer objects, using loaded options when present. */
export function multiChoiceAnswersFromDefaults(
  defaults: readonly string[],
  options: readonly ChoiceOption[],
): ChoiceCollectionData[] {
  return defaults.map((key) => {
    const option = options.find((item) => (item.data?.id ?? item.value) === key);
    if (option) {
      return choiceOptionToAnswer(option);
    }
    return { id: key, uniqueKey: '', display: '' };
  });
}

@Component({
  selector: 'app-multi-choice',
  imports: [BaseSignalInput],
  templateUrl: './multi-choice.html',
  styleUrl: './multi-choice.css',
})
export class MultiChoice extends BaseFormControl<MultiChoiceValue> {
  readonly label = input('');
  readonly helpText = input('');
  options = input.required<ChoiceOption[]>();
  /** When true, store `{ id, uniqueKey, display }[]`; otherwise store `string[]`. */
  readonly collectionAnswers = input(false);

  /** Disabled and readonly both block interaction. Only disabled fades the options. */
  readonly isInteractionBlocked = computed(
    () => this.disabled() || this.readonly(),
  );

  toggleOption(option: ChoiceOption): void {
    if (this.isInteractionBlocked()) {
      return;
    }

    if (this.collectionAnswers()) {
      const id = option.data?.id ?? option.value;
      const raw = this.value();
      const current: ChoiceCollectionData[] = isMultiChoiceAnswer(raw) ? raw : [];
      const next = current.some((item) => item.id === id)
        ? current.filter((item) => item.id !== id)
        : [...current, choiceOptionToAnswer(option)];
      this.value.set(next);
      return;
    }

    const key = option.value;
    const raw = this.value();
    const current: string[] = isMultiChoiceStringAnswer(raw) ? raw : [];
    const next = current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key];
    this.value.set(next);
  }

  isSelected(option: ChoiceOption): boolean {
    if (this.collectionAnswers()) {
      const id = option.data?.id ?? option.value;
      const raw = this.value();
      const current: ChoiceCollectionData[] = isMultiChoiceAnswer(raw) ? raw : [];
      return current.some((item) => item.id === id);
    }

    const raw = this.value();
    const current: string[] = isMultiChoiceStringAnswer(raw) ? raw : [];
    return current.includes(option.value);
  }
}
